import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as RouterLink, useLocation} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {RepoIssuesSidebarLayoutEnabledContext} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {SavedViewsResponse, UserShortcut} from '@github-ui/saved-views'
import {canShowCreateAction} from '@github-ui/saved-views/utils/can-show-create-action'
import {ViewsBackLink} from '@github-ui/saved-views/components/ViewsBackLink'
import {BUTTON_LABELS} from '@github-ui/saved-views/constants'
import {RepositorySavedViewContent} from '@github-ui/saved-views/components/detail/RepositorySavedViewContent'
import {ViewsIndexContent} from '@github-ui/saved-views/components/index/ViewsIndexContent'
import {RepositoryViewNewContent} from '@github-ui/saved-views/components/new/RepositoryViewNewContent'
import {useConfirmDiscardChanges} from '@github-ui/saved-views/hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '@github-ui/saved-views/hooks/use-saved-view-editor'
import {useNavigate} from '@github-ui/use-navigate'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {SavedViewBoard} from '../components/board/SavedViewBoard'
import {getDashboardRelayEnvironment} from '../components/dashboard-relay-environment'
import {readBoardParams} from '../utils/board-url-params'
import {issuesLayoutRoute} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {savedViewsRoute} from './saved-views-routes'
import {TanStackSavedViewIssues} from './TanStackSavedViewIssues'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue state:open',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

function useSavedViewsLayout(initialSavedViews?: SavedViewsResponse) {
  const {owner, repo} = useRouteParams(issuesLayoutRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  return {
    owner,
    repo,
    layoutPayload,
    renderSidebar: useRenderSidebar(owner, repo, layoutPayload, initialSavedViews),
  }
}

export function SavedViews() {
  const {data: savedViews} = useRouteQuery(savedViewsRoute, 'mainQuery')
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout(savedViews)
  const newViewUrl = `/${owner}/${repo}/issues/views/new`
  const teamPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const newViewAction = canShowCreateAction({savedViews, teamPersonalEnabled}) ? (
    <Button as={RouterLink} to={newViewUrl} variant="primary">
      {BUTTON_LABELS.newView}
    </Button>
  ) : undefined

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        title="Views"
        dialogTitle="Views"
        actions={newViewAction}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <ViewsIndexContent
          owner={owner}
          repo={repo}
          newViewUrl={newViewUrl}
          pinEnabled={layoutPayload?.pinnedViewsEnabled ?? false}
          teamPersonalEnabled={teamPersonalEnabled}
          initialData={savedViews}
        />
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedViewNew() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const navigate = useNavigate()
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty: editor.isDirty})
  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleBackClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    async event => {
      if (event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !editor.isDirty) return

      event.preventDefault()
      if (await confirmDiscardIfDirty()) navigate(viewsPath)
    },
    [confirmDiscardIfDirty, editor.isDirty, navigate, viewsPath],
  )

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        dialogTitle="Views"
        title="Create view"
        contextArea={<ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <RepositoryViewNewContent
          editor={editor}
          confirmDiscardIfDirty={confirmDiscardIfDirty}
          onBackLinkClick={handleBackClick}
          IssuesRenderer={TanStackSavedViewIssues}
        />
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedView() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const {search} = useLocation()
  const boardEnabled = isFeatureEnabled('repo_issues_board_view')
  const layout = useMemo(() => readBoardParams(new URLSearchParams(search)).layout, [search])
  const IssuesRenderer = boardEnabled && layout === 'board' ? SavedViewBoard : TanStackSavedViewIssues

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepositorySavedViewContent
        IssuesRenderer={IssuesRenderer}
        pinEnabled={layoutPayload?.pinnedViewsEnabled ?? false}
        renderSidebar={renderSidebar}
        renderCreateIssueButton={() => <RepositoryCreateIssueButton owner={owner} repo={repo} />}
      />
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

function RepositoryCreateIssueButton({owner, repo}: {owner: string; repo: string}) {
  const environment = getDashboardRelayEnvironment()
  const navigate = useNavigate()

  return (
    <RelayEnvironmentProvider environment={environment}>
      <CreateIssueButton
        label="New issue"
        navigate={navigate}
        optionConfig={{
          issueCreateArguments: {repository: {owner, name: repo}},
          showRepositoryPicker: false,
          showFullScreenButton: true,
          canBypassTemplateSelection: true,
          navigate,
        }}
      />
    </RelayEnvironmentProvider>
  )
}
